import React, { useEffect, useMemo, useRef, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./calendarOverrides.css";
import { db } from "../firebase/firebaseConfig";
import {
  availableDateKeys,
  filterReportsByDateKey,
  findReportsBySerial,
  formatReportDate,
  localDateKey,
  matchReportNumber,
  normalizeDetailItems,
  normalizeReport,
  reconcileReportPresence,
} from "./despatchViewHelper";

const REPORTS_PER_PAGE = 25;

function friendlyError(error, action) {
  if (error?.code === "permission-denied") {
    return `Access denied while ${action}. Your account needs the admin or despatch role.`;
  }
  return error?.message
    ? `Could not complete ${action}: ${error.message}`
    : `Could not complete ${action}. Please try again.`;
}

function sortReports(reports) {
  return [...reports].sort((a, b) => {
    if (a.createdAt && b.createdAt) return b.createdAt - a.createdAt;
    if (a.createdAt) return -1;
    if (b.createdAt) return 1;
    return a.reportNumber.localeCompare(b.reportNumber);
  });
}

export default function DespatchView() {
  const navigate = useNavigate();
  const requestIdRef = useRef(0);
  const detailsCacheRef = useRef(null);

  const [allReports, setAllReports] = useState([]);
  const [detailsById, setDetailsById] = useState(null);
  const [loading, setLoading] = useState(true);
  const [serialLoading, setSerialLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedDateKey, setSelectedDateKey] = useState("");
  const [searchSerial, setSearchSerial] = useState("");
  const [activeSerial, setActiveSerial] = useState("");
  const [searchReportNo, setSearchReportNo] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError("");

    getDocs(collection(db, "despatchreports"))
      .then((snapshot) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        const reports = snapshot.docs.map((document) =>
          normalizeReport(document)
        );
        setAllReports(sortReports(reports));
      })
      .catch((loadError) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        console.error("Error fetching despatch reports:", loadError);
        setError(friendlyError(loadError, "loading despatch reports"));
      })
      .finally(() => {
        if (!cancelled && requestId === requestIdRef.current) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      requestIdRef.current += 1;
    };
  }, []);

  const dateKeys = useMemo(
    () => availableDateKeys(allReports),
    [allReports]
  );

  const reconciliation = useMemo(
    () => reconcileReportPresence(allReports, detailsById),
    [allReports, detailsById]
  );

  const filteredReports = useMemo(() => {
    let filtered = filterReportsByDateKey(allReports, selectedDateKey);

    if (activeSerial && detailsById instanceof Map) {
      filtered = findReportsBySerial({
        reports: filtered,
        detailsById,
        term: activeSerial,
      }).reports;
    }

    if (searchReportNo.trim()) {
      const exact = [];
      const partial = [];
      for (const report of filtered) {
        const match = matchReportNumber(report, searchReportNo);
        if (!match.matches) continue;
        (match.exact ? exact : partial).push(report);
      }
      filtered = [...exact, ...partial];
    }

    return filtered;
  }, [
    activeSerial,
    allReports,
    detailsById,
    searchReportNo,
    selectedDateKey,
  ]);

  // Record-integrity findings are diagnostic only: they are logged to the
  // browser console for anyone investigating data problems, and intentionally
  // NOT shown in the UI (a normal user does not need to see them).
  useEffect(() => {
    if (!(detailsById instanceof Map)) return;
    const {
      summaryWithoutDetail,
      detailWithoutSummary,
      duplicateReportNumbers,
    } = reconciliation;
    if (
      summaryWithoutDetail.length ||
      detailWithoutSummary.length ||
      duplicateReportNumbers.length
    ) {
      console.warn("Despatch record integrity issues detected:", {
        summaryWithoutDetail,
        detailWithoutSummary,
        duplicateReportNumbers,
      });
    }
  }, [detailsById, reconciliation]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredReports.length / REPORTS_PER_PAGE)
  );

  useEffect(() => {
    setCurrentPage((page) => Math.min(Math.max(1, page), totalPages));
  }, [totalPages]);

  const paginatedReports = filteredReports.slice(
    (currentPage - 1) * REPORTS_PER_PAGE,
    currentPage * REPORTS_PER_PAGE
  );

  const busy = loading || serialLoading;
  const hasFilters = Boolean(
    selectedDateKey || activeSerial || searchReportNo.trim()
  );
  const activeFilters = [
    selectedDateKey ? `Date: ${selectedDateKey}` : "",
    activeSerial ? `Serial: ${activeSerial.trim()}` : "",
    searchReportNo.trim()
      ? `Report number: ${searchReportNo.trim()}`
      : "",
  ].filter(Boolean);

  const searchBySerial = async (event) => {
    event?.preventDefault();
    const term = searchSerial.trim();
    setError("");
    setCurrentPage(1);

    if (!term) {
      setActiveSerial("");
      return;
    }

    if (detailsCacheRef.current instanceof Map) {
      setDetailsById(detailsCacheRef.current);
      setActiveSerial(term);
      return;
    }

    const requestId = ++requestIdRef.current;
    setSerialLoading(true);
    try {
      const snapshot = await getDocs(
        collection(db, "despatchreportsdetailed")
      );
      if (requestId !== requestIdRef.current) return;

      const cache = new Map(
        snapshot.docs.map((document) => [
          document.id,
          normalizeDetailItems(document),
        ])
      );
      detailsCacheRef.current = cache;
      setDetailsById(cache);
      setActiveSerial(term);
    } catch (searchError) {
      if (requestId !== requestIdRef.current) return;
      console.error("Error searching despatch serials:", searchError);
      setError(friendlyError(searchError, "searching serial numbers"));
    } finally {
      if (requestId === requestIdRef.current) setSerialLoading(false);
    }
  };

  const resetFilters = () => {
    requestIdRef.current += 1;
    setSerialLoading(false);
    setError("");
    setSelectedDateKey("");
    setSearchSerial("");
    setActiveSerial("");
    setSearchReportNo("");
    setCurrentPage(1);
  };

  const chooseDate = (date) => {
    if (busy) return;
    setSelectedDateKey(localDateKey(date));
    setCurrentPage(1);
  };

  const openReport = (reportId) => {
    navigate(`/DespatchDetailView/${reportId}`);
  };

  const emptyDatabase = !loading && !error && allReports.length === 0;
  const noMatches =
    !loading &&
    !error &&
    allReports.length > 0 &&
    filteredReports.length === 0;

  return (
    <main className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-6xl space-y-5">
        <header>
          <h1 className="text-2xl font-semibold text-gray-900">
            Despatch Reports
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Search report summaries and open their scanned-item details.
          </p>
        </header>

        <section className="grid grid-cols-1 gap-5 lg:grid-cols-[auto,1fr]">
          <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
            <Calendar
              onClickDay={chooseDate}
              calendarType="gregory"
              tileDisabled={() => busy}
              tileContent={({ date, view }) => {
                if (view !== "month") return null;
                if (!dateKeys.has(localDateKey(date))) return null;
                return (
                  <div className="text-center">
                    <span className="mx-auto mt-1 block h-1 w-1 rounded-full bg-primary" />
                  </div>
                );
              }}
            />
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
            <form onSubmit={searchBySerial} className="space-y-4">
              <label className="block text-sm font-semibold text-gray-700">
                Serial number
                <div className="mt-1 flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter Serial Number"
                    value={searchSerial}
                    onChange={(event) => setSearchSerial(event.target.value)}
                    disabled={busy}
                    className="w-full rounded-md border p-3 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="rounded-md bg-primary px-4 py-3 font-semibold text-white hover:bg-hover disabled:opacity-50"
                  >
                    {serialLoading ? "Searching…" : "Search"}
                  </button>
                </div>
              </label>

              <label className="block text-sm font-semibold text-gray-700">
                Report number
                <input
                  type="text"
                  placeholder="Enter Report Number"
                  value={searchReportNo}
                  onChange={(event) => {
                    setSearchReportNo(event.target.value);
                    setCurrentPage(1);
                  }}
                  disabled={busy}
                  className="mt-1 w-full rounded-md border p-3 focus:outline-none focus:ring-2 focus:ring-primary disabled:bg-gray-100"
                />
              </label>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={resetFilters}
                  disabled={loading || (!hasFilters && !serialLoading)}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                >
                  Show all / Reset filters
                </button>
                <span className="text-sm text-gray-600">
                  {filteredReports.length} result
                  {filteredReports.length === 1 ? "" : "s"}
                </span>
              </div>
            </form>

            <div className="mt-4 min-h-6 text-sm text-gray-600">
              {activeFilters.length ? (
                <span>Active filters: {activeFilters.join(" · ")}</span>
              ) : (
                <span>No active filters.</span>
              )}
            </div>
          </div>
        </section>

        {error ? (
          <section
            role="alert"
            className="rounded-lg border border-red-300 bg-red-50 p-4 text-red-800"
          >
            <div className="font-semibold">Despatch reports unavailable</div>
            <div className="mt-1 text-sm">{error}</div>
          </section>
        ) : null}

        {loading ? (
          <section className="rounded-lg border border-blue-200 bg-blue-50 p-6 text-center text-blue-700">
            Loading despatch reports…
          </section>
        ) : null}

        {emptyDatabase ? (
          <section className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div className="font-semibold text-gray-800">
              No despatch reports exist.
            </div>
            <div className="mt-1 text-sm text-gray-500">
              The database returned no report summaries.
            </div>
          </section>
        ) : null}

        {noMatches ? (
          <section className="rounded-lg border border-gray-200 bg-white p-8 text-center">
            <div className="font-semibold text-gray-800">
              No reports match the current filters.
            </div>
            <button
              type="button"
              onClick={resetFilters}
              className="mt-3 text-sm font-semibold text-primary hover:underline"
            >
              Show all reports
            </button>
          </section>
        ) : null}

        {!loading && !error && paginatedReports.length ? (
          <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-primary text-white">
                  <tr>
                    <th className="px-4 py-3 text-left">Report #</th>
                    <th className="px-4 py-3 text-left">Created At</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedReports.map((report) => (
                      <tr
                        key={report.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openReport(report.id)}
                        onKeyDown={(event) => {
                          if (
                            event.key === "Enter" ||
                            event.key === " "
                          ) {
                            event.preventDefault();
                            openReport(report.id);
                          }
                        }}
                        className="cursor-pointer text-left hover:bg-primary/5 focus:bg-primary/5 focus:outline-none"
                      >
                        <td className="px-4 py-3 font-semibold text-gray-900">
                          {report.reportNumber || "—"}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {formatReportDate(report.createdAt) || "—"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                              report.completed
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {report.completed ? "Completed" : "Pending"}
                          </span>
                        </td>
                      </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) => Math.max(1, page - 1))
                }
                disabled={currentPage <= 1}
                className="rounded bg-gray-200 px-4 py-2 hover:bg-gray-300 disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCurrentPage((page) =>
                    Math.min(totalPages, page + 1)
                  )
                }
                disabled={currentPage >= totalPages}
                className="rounded bg-gray-200 px-4 py-2 hover:bg-gray-300 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </section>
        ) : null}
      </div>
    </main>
  );
}
